import { useState, useRef, useCallback } from "react";
import { Upload, Link2, Wand2, X, Image, AlertTriangle, Info, Loader2, Replace, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import axios from "axios";
import { API } from "@/App";

const TABS = [
  { id: "upload", label: "Upload", icon: Upload },
  { id: "url", label: "URL", icon: Link2 },
  { id: "ai", label: "Generate with AI", icon: Wand2 },
];

export function ImageSourceSelector({
  value,
  onChange,
  sizeGuide,
  label,
  deviceLabel,
  maxFileSize = 4,
  accept = ".webp,.jpg,.jpeg,.png",
  className,
}) {
  const [tab, setTab] = useState("upload");
  const [urlInput, setUrlInput] = useState("");
  const [aiPrompt, setAiPrompt] = useState("");
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [uploadInfo, setUploadInfo] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const fileRef = useRef(null);

  const maxBytes = maxFileSize * 1024 * 1024;

  const handleFile = useCallback(async (file) => {
    setError(null);
    if (!file) return;
    const allowed = ["image/webp", "image/jpeg", "image/png", "image/jpg"];
    if (!allowed.includes(file.type)) {
      setError("Unsupported format. Allowed: WebP, JPG, PNG");
      return;
    }
    if (file.size > maxBytes) {
      setError(`File too large. Maximum is ${maxFileSize}MB`);
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/admin/media/upload`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      const asset = res.data.asset;
      onChange(asset.url);
      setUploadInfo({
        width: asset.width,
        height: asset.height,
        size: asset.sizeBytes,
        format: asset.mimeType?.split("/")[1]?.toUpperCase(),
        filename: asset.originalFilename,
      });
      // Validate dimensions if size guide is provided
      if (sizeGuide?.minWidth && asset.width && asset.width < sizeGuide.minWidth) {
        setError(`Image is ${asset.width}×${asset.height}px. Minimum recommended: ${sizeGuide.minWidth}×${sizeGuide.minHeight}px. It may appear blurry.`);
      }
    } catch (e) {
      setError(e.response?.data?.detail || "Upload failed");
    }
    setUploading(false);
  }, [onChange, maxBytes, maxFileSize, sizeGuide]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer.files?.[0]) handleFile(e.dataTransfer.files[0]);
  }, [handleFile]);

  const handleUrlSubmit = () => {
    if (!urlInput.trim()) return;
    onChange(urlInput.trim());
    setUploadInfo(null);
    setError(null);
  };

  const handleAiGenerate = async () => {
    if (!aiPrompt.trim()) return;
    setGenerating(true);
    setError(null);
    try {
      const res = await axios.post(`${API}/admin/generate-image`, { prompt: aiPrompt }, { withCredentials: true });
      onChange(res.data.image_url);
      setUploadInfo(null);
    } catch (e) {
      setError(e.response?.data?.detail || "Image generation failed");
    }
    setGenerating(false);
  };

  const handleRemove = () => {
    onChange("");
    setUploadInfo(null);
    setError(null);
    setUrlInput("");
  };

  return (
    <div className={cn("space-y-3", className)} data-testid={`image-selector-${deviceLabel || label || "default"}`}>
      {/* Label + Size Guide */}
      <div className="flex items-center justify-between">
        <label className="text-xs font-medium text-slate-400">{label || "Image"}</label>
        {deviceLabel && <span className="text-[10px] text-slate-600 uppercase tracking-wider">{deviceLabel}</span>}
      </div>

      {sizeGuide && (
        <div className="flex items-start gap-2 px-3 py-2 bg-slate-800/30 border border-slate-700/30 rounded-lg">
          <Info size={12} className="text-blue-400 mt-0.5 flex-shrink-0" />
          <div className="text-[11px] text-slate-500 leading-relaxed">
            <span className="text-slate-400 font-medium">Recommended: {sizeGuide.width} × {sizeGuide.height} px</span>
            {sizeGuide.minWidth && <span className="ml-2">· Min: {sizeGuide.minWidth}×{sizeGuide.minHeight}</span>}
            {sizeGuide.ratio && <span className="ml-2">· Ratio: {sizeGuide.ratio}</span>}
            <span className="ml-2">· Max: {maxFileSize}MB</span>
            <span className="ml-2">· WebP, JPG, PNG</span>
          </div>
        </div>
      )}

      {/* Preview */}
      {value && (
        <div className="relative rounded-lg overflow-hidden border border-slate-700/50 bg-slate-900">
          <img
            src={value.startsWith("/api") ? `${window.location.origin}${value}` : value}
            alt="" className="w-full max-h-40 object-contain bg-[#0a0e1a]"
            onError={(e) => { e.target.style.display = "none"; }}
          />
          <div className="absolute top-2 right-2 flex gap-1">
            <button onClick={() => { setTab("upload"); fileRef.current?.click(); }}
              className="p-1.5 rounded-lg bg-black/70 backdrop-blur text-white hover:bg-black/90 text-xs" title="Replace">
              <Replace size={12} />
            </button>
            <button onClick={handleRemove}
              className="p-1.5 rounded-lg bg-black/70 backdrop-blur text-red-400 hover:bg-black/90" title="Remove">
              <X size={12} />
            </button>
          </div>
          {uploadInfo && (
            <div className="px-3 py-1.5 bg-slate-800/90 text-[10px] text-slate-400 flex gap-3 border-t border-slate-700/50">
              {uploadInfo.width && <span>{uploadInfo.width}×{uploadInfo.height}px</span>}
              {uploadInfo.size && <span>{(uploadInfo.size / 1024).toFixed(0)}KB</span>}
              {uploadInfo.format && <span>{uploadInfo.format}</span>}
            </div>
          )}
        </div>
      )}

      {/* Error/Warning */}
      {error && (
        <div className="flex items-start gap-2 px-3 py-2 bg-amber-500/10 border border-amber-500/20 rounded-lg" data-testid="image-upload-warning">
          <AlertTriangle size={12} className="text-amber-400 mt-0.5 flex-shrink-0" />
          <div className="text-[11px] text-amber-300 leading-relaxed">
            {error}
            <div className="flex gap-2 mt-1.5">
              <button onClick={() => { setError(null); fileRef.current?.click(); }}
                className="text-[10px] text-blue-400 hover:underline">Upload Another</button>
              <button onClick={() => setError(null)}
                className="text-[10px] text-slate-500 hover:underline">Continue Anyway</button>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      {!value && (
        <>
          <div className="flex gap-1 bg-slate-800/50 rounded-lg p-0.5">
            {TABS.map(t => (
              <button key={t.id} onClick={() => setTab(t.id)}
                className={cn("flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-[11px] font-medium transition-all",
                  tab === t.id ? "bg-slate-700 text-white shadow-sm" : "text-slate-500 hover:text-slate-300")}
                data-testid={`image-tab-${t.id}`}>
                <t.icon size={12} />{t.label}
              </button>
            ))}
          </div>

          {/* Upload Tab */}
          {tab === "upload" && (
            <div
              className={cn(
                "border-2 border-dashed rounded-xl p-6 text-center transition-colors cursor-pointer",
                dragActive ? "border-blue-500 bg-blue-500/5" : "border-slate-700/50 hover:border-slate-600",
                uploading && "pointer-events-none opacity-50"
              )}
              onDragOver={e => { e.preventDefault(); setDragActive(true); }}
              onDragLeave={() => setDragActive(false)}
              onDrop={handleDrop}
              onClick={() => fileRef.current?.click()}
              data-testid="image-drop-zone"
            >
              {uploading ? (
                <div className="flex flex-col items-center gap-2">
                  <Loader2 size={20} className="text-blue-400 animate-spin" />
                  <span className="text-xs text-slate-400">Uploading...</span>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <Upload size={20} className="text-slate-500" />
                  <span className="text-xs text-slate-400">Drag and drop your image here</span>
                  <span className="text-[10px] text-slate-600">or</span>
                  <span className="text-xs text-blue-400 font-medium">Choose Image</span>
                </div>
              )}
              <input ref={fileRef} type="file" accept={accept} className="hidden"
                onChange={e => { if (e.target.files?.[0]) handleFile(e.target.files[0]); e.target.value = ""; }}
              />
            </div>
          )}

          {/* URL Tab */}
          {tab === "url" && (
            <div className="flex gap-2">
              <input type="text" value={urlInput} onChange={e => setUrlInput(e.target.value)}
                placeholder="https://example.com/image.jpg"
                className="flex-1 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white focus:border-blue-500 focus:outline-none"
                data-testid="image-url-input"
                onKeyDown={e => { if (e.key === "Enter") handleUrlSubmit(); }}
              />
              <button onClick={handleUrlSubmit} disabled={!urlInput.trim()}
                className="px-3 py-2 rounded-lg bg-blue-600 text-white text-xs font-medium disabled:opacity-40">
                <Check size={14} />
              </button>
            </div>
          )}

          {/* AI Generate Tab */}
          {tab === "ai" && (
            <div className="space-y-2">
              <textarea rows={3} value={aiPrompt} onChange={e => setAiPrompt(e.target.value)}
                placeholder="Describe the image you want..."
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white focus:border-blue-500 focus:outline-none resize-none"
                data-testid="image-ai-prompt"
              />
              <button onClick={handleAiGenerate} disabled={generating || !aiPrompt.trim()}
                className="w-full px-4 py-2.5 rounded-lg bg-purple-600 text-white text-sm font-medium hover:bg-purple-500 disabled:opacity-40 flex items-center justify-center gap-2"
                data-testid="image-ai-generate-btn">
                {generating ? <><Loader2 size={14} className="animate-spin" /> Generating...</> : <><Wand2 size={14} /> Generate Image</>}
              </button>
            </div>
          )}
        </>
      )}

      {/* Hidden file input for replace */}
      <input ref={fileRef} type="file" accept={accept} className="hidden"
        onChange={e => { if (e.target.files?.[0]) handleFile(e.target.files[0]); e.target.value = ""; }}
      />
    </div>
  );
}

// ===== SIZE GUIDE CONFIGS =====

export const SLIDER_SIZE_GUIDES = {
  desktop: { width: 1920, height: 760, minWidth: 1600, minHeight: 633, ratio: "~2.53:1" },
  tablet: { width: 1400, height: 800, minWidth: 1200, minHeight: 686, ratio: "~1.75:1" },
  mobile: { width: 900, height: 1200, minWidth: 750, minHeight: 1000, ratio: "3:4" },
};

const WIDE_PROMO = {
  desktop: { width: 1600, height: 320, minWidth: 1200, minHeight: 240, ratio: "5:1" },
  tablet: { width: 1200, height: 360, minWidth: 1000, minHeight: 300, ratio: "~3.33:1" },
  mobile: { width: 900, height: 600, minWidth: 750, minHeight: 500, ratio: "3:2" },
};
const ANNOUNCEMENT = {
  desktop: { width: 1600, height: 160, minWidth: 1200, minHeight: 120, ratio: "10:1" },
  tablet: { width: 1200, height: 160, minWidth: 1000, minHeight: 120, ratio: "7.5:1" },
  mobile: { width: 900, height: 240, minWidth: 750, minHeight: 200, ratio: "3.75:1" },
};
const FEATURE = {
  desktop: { width: 1600, height: 500, minWidth: 1200, minHeight: 375, ratio: "~3.2:1" },
  tablet: { width: 1200, height: 500, minWidth: 1000, minHeight: 417, ratio: "~2.4:1" },
  mobile: { width: 900, height: 900, minWidth: 750, minHeight: 750, ratio: "1:1" },
};
const PREMIUM = {
  desktop: { width: 1600, height: 300, minWidth: 1200, minHeight: 225, ratio: "~5.3:1" },
  tablet: { width: 1200, height: 340, minWidth: 1000, minHeight: 283, ratio: "~3.5:1" },
  mobile: { width: 900, height: 620, minWidth: 750, minHeight: 517, ratio: "~1.45:1" },
};
const FOOTER_BANNER = {
  desktop: { width: 1600, height: 260, minWidth: 1200, minHeight: 195, ratio: "~6.15:1" },
  tablet: { width: 1200, height: 300, minWidth: 1000, minHeight: 250, ratio: "4:1" },
  mobile: { width: 900, height: 560, minWidth: 750, minHeight: 467, ratio: "~1.6:1" },
};

export function getBannerSizeGuide(type, placement) {
  if (placement === "homepage_top" || placement === "global_top") return ANNOUNCEMENT;
  if (placement === "footer" || placement === "global_bottom") return FOOTER_BANNER;
  if (type === "premium" || type === "subscription") return PREMIUM;
  if (type === "category" || type === "channel" || placement === "category_page" || placement === "channel_page") return FEATURE;
  return WIDE_PROMO;
}
